import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import dosier from "@/assets/dosier-retail.pdf.asset.json";
import wordmarkWhite from "@/assets/techtail-wordmark-pixel.png";
import { submitLead } from "@/lib/leadSubmit";
import { divisionFromPath, divisionFromSpace, isAcceptableEmail, isDisposableEmail, isValidPhone } from "@/lib/leadGuard";
import { countryNameEs, isCountryCode } from "@/lib/countries";
import { CountrySelect, HoneypotField } from "./LeadFields";
import { useLeadSuccess } from "@/hooks/useLeadSuccess";

const PixelArrow = () => (
  <svg viewBox="0 0 12 12" className="h-4 w-4" fill="currentColor" shapeRendering="crispEdges">
    <rect x="1" y="5" width="7" height="2" />
    <rect x="7" y="3" width="2" height="1" />
    <rect x="7" y="8" width="2" height="1" />
    <rect x="9" y="5" width="2" height="2" />
  </svg>
);

type Answers = {
  nombre: string;
  espacio: string;
  espacioOtra: string;
  canalOtra: string;
  ubicaciones: string;
  objetivos: string[];
  plazo: string;
  decide: string;
  quienDecide: string;
  empresa: string;
  cargo: string;
  ciudad: string;
  pais: string;
  email: string;
  telefono: string;
  canal: string;
  extra: string;
};

const emptyAnswers: Answers = {
  nombre: "",
  espacio: "",
  espacioOtra: "",
  canalOtra: "",
  ubicaciones: "",
  objetivos: [],
  plazo: "",
  decide: "",
  quienDecide: "",
  empresa: "",
  cargo: "",
  ciudad: "",
  pais: "",
  email: "",
  telefono: "",
  canal: "",
  extra: "",
};

// Canonical codes stored with the lead (same order as the option arrays).
const ESPACIO_CODES = ["retail-red", "hospitality", "industria", "hospital", "otro"];
const UBICACION_CODES = ["1", "2-10", "10+", "red", "valorando"];
const PLAZO_CODES = ["listo", "<3m", "este-ano", "proximo-ano", "info"];

const ESPACIOS = [
  "Un punto de venta o una red de tiendas",
  "Hoteles, gimnasios o coworkings",
  "Un almacén industrial",
  "Un almacén clínico u hospitalario",
  "Otra cosa",
];

const UBICACIONES = [
  "Una, para empezar",
  "De 2 a 10",
  "Más de 10",
  "Toda la red",
  "Todavía lo estoy valorando",
];

const OBJETIVOS_RETAIL = [
  "Abrir más horas sin ampliar plantilla",
  "Dejar de depender de encontrar personal",
  "Saber en tiempo real qué pasa en cada punto",
  "Mejorar el margen por ubicación",
  "Abrir en sitios donde no cabe una tienda",
];

const OBJETIVOS_ALMACEN = [
  "Saber dónde va cada euro de material",
  "Acabar con los recuentos manuales",
  "Evitar roturas y compras de urgencia",
  "Asignar el coste real a cada proyecto",
  "Trazabilidad de lote y caducidad",
];

const PLAZOS = [
  "Tengo presupuesto y quiero avanzar",
  "Dentro de tres meses",
  "Este año",
  "El año que viene",
  "Estoy explorando",
];

const QUIEN_DECIDE = [
  "Dirección general",
  "Operaciones",
  "Compras",
  "Un comité",
  "Todavía no está claro",
];

const CANALES = [
  "Alguien me lo recomendó",
  "LinkedIn",
  "Lo leí en prensa",
  "Buscando en Google",
  "Una feria o un evento",
  "Entré en una tienda y me quedé con la duda",
  "Otra cosa",
];

const DECIDE_OPCIONES = ["Sí", "No"];

type StepId =
  | "intro"
  | "nombre"
  | "espacio"
  | "ubicaciones"
  | "objetivos"
  | "plazo"
  | "decide"
  | "quienDecide"
  | "empresa"
  | "email"
  | "canal"
  | "extra"
  | "final";

interface Props {
  open: boolean;
  onClose: () => void;
}

const OptionButton = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left border px-5 py-4 text-base md:text-lg transition-colors ${
      selected
        ? "border-brand-blue bg-brand-blue text-black"
        : "border-white/25 text-white hover:border-white hover:bg-white/5"
    }`}
  >
    {label}
  </button>
);

const inputClass =
  "w-full border-b border-white/30 bg-transparent py-3 text-xl md:text-2xl text-white placeholder:text-white/35 focus:border-brand-blue focus:outline-none";

const Question = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="w-full max-w-2xl">
    <h2 className="font-serif text-2xl md:text-4xl leading-tight text-white">{title}</h2>
    {hint && <p className="mt-3 text-sm md:text-base text-white/50">{hint}</p>}
    <div className="mt-8 space-y-3">{children}</div>
  </div>
);

const TransformForm = ({ open, onClose }: Props) => {
  const onSuccess = useLeadSuccess();
  const { t, i18n } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        nombre: z.string().trim().min(1).max(100),
        empresa: z.string().trim().min(1).max(100),
        email: z.string().trim().max(255).refine(isAcceptableEmail),
        telefono: z.string().trim().max(30).refine(isValidPhone),
        cargo: z.string().trim().max(100),
        ciudad: z.string().trim().min(1).max(100),
        pais: z.string().trim().refine(isCountryCode),
        extra: z.string().trim().max(1000),
      }),
    []
  );

  const espaciosLabels = t("sFormsTransform.espacios", { returnObjects: true }) as string[];
  const ubicacionesLabels = t("sFormsTransform.ubicacionesOpciones", { returnObjects: true }) as string[];
  const objetivosRetailLabels = t("sFormsTransform.objetivosRetail", { returnObjects: true }) as string[];
  const objetivosAlmacenLabels = t("sFormsTransform.objetivosAlmacen", { returnObjects: true }) as string[];
  const plazosLabels = t("sFormsTransform.plazos", { returnObjects: true }) as string[];
  const decideLabels = t("sFormsTransform.decideOpciones", { returnObjects: true }) as string[];
  const quienDecideLabels = t("sFormsTransform.quienDecideOpciones", { returnObjects: true }) as string[];
  const canalesLabels = t("sFormsTransform.canales", { returnObjects: true }) as string[];

  const [a, setA] = useState<Answers>(emptyAnswers);
  const [index, setIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const startedAt = useRef(0);
  const { pathname } = useLocation();

  const isAlmacen =
    a.espacio === "Un almacén industrial" || a.espacio === "Un almacén clínico u hospitalario";

  const steps = useMemo<StepId[]>(() => {
    const base: StepId[] = [
      "intro",
      "nombre",
      "espacio",
      "ubicaciones",
      "objetivos",
      "plazo",
      "decide",
    ];
    if (a.decide === "No") base.push("quienDecide");
    return [...base, "empresa", "email", "canal", "extra", "final"];
  }, [a.decide]);

  const step = steps[Math.min(index, steps.length - 1)];
  const total = steps.length - 2; // exclude intro + final

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setA(emptyAnswers);
      setIndex(0);
      setError(null);
      setHoneypot("");
      startedAt.current = Date.now();
    }
  }, [open]);

  const next = () => setIndex((i) => Math.min(i + 1, steps.length - 1));
  const back = () => setIndex((i) => Math.max(i - 1, 0));

  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setA((p) => ({ ...p, [k]: v }));

  const pick = <K extends keyof Answers>(k: K, v: Answers[K]) => {
    set(k, v);
    setTimeout(next, 180);
  };

  const toggleObjetivo = (v: string) =>
    setA((p) => ({
      ...p,
      objetivos: p.objetivos.includes(v)
        ? p.objetivos.filter((x) => x !== v)
        : [...p.objetivos, v],
    }));

  const canContinue = () => {
    switch (step) {
      case "nombre":
        return a.nombre.trim().length > 0;
      case "objetivos":
        return a.objetivos.length > 0;
      case "empresa":
        return a.empresa.trim().length > 0 && a.ciudad.trim().length > 0 && a.pais !== "";
      case "email":
        return isAcceptableEmail(a.email) && isValidPhone(a.telefono);
      default:
        return true;
    }
  };

  const submit = async () => {
    const parsed = schema.safeParse({
      nombre: a.nombre,
      empresa: a.empresa,
      email: a.email,
      telefono: a.telefono,
      cargo: a.cargo,
      ciudad: a.ciudad,
      pais: a.pais,
      extra: a.extra,
    });
    if (!parsed.success) {
      setError(t("sFormsTransform.errors.invalid"));
      return;
    }

    setSending(true);
    setError(null);

    const mensaje = [
      `Espacio: ${a.espacio === "Otra cosa" ? `Otra cosa — ${a.espacioOtra || "—"}` : a.espacio}`,
      `Ubicaciones: ${a.ubicaciones}`,
      `Objetivos: ${a.objetivos.join(" · ")}`,
      `Plazo: ${a.plazo}`,
      `¿Decide? ${a.decide}${a.decide === "No" && a.quienDecide ? ` (${a.quienDecide})` : ""}`,
      `Cargo: ${a.cargo || "—"}`,
      `Canal: ${a.canal === "Otra cosa" ? `Otra cosa — ${a.canalOtra || "—"}` : a.canal}`,

      a.extra ? `Comentario: ${a.extra}` : "",
    ]
      .filter(Boolean)
      .join("\n")
      .slice(0, 1000);

    const code = (codes: string[], list: string[], v: string) => codes[list.indexOf(v)] ?? null;
    const tipo_espacio = code(ESPACIO_CODES, ESPACIOS, a.espacio);
    const qualification = {
      tipo_espacio,
      num_ubicaciones: code(UBICACION_CODES, UBICACIONES, a.ubicaciones),
      plazo: code(PLAZO_CODES, PLAZOS, a.plazo),
      request_type: "automatizar",
    };
    const division = divisionFromSpace(tipo_espacio ?? undefined, divisionFromPath(pathname));

    const res = await submitLead({
      core: {
        nombre: parsed.data.nombre,
        empresa: parsed.data.empresa,
        email: parsed.data.email,
        telefono: parsed.data.telefono,
        pais: countryNameEs(parsed.data.pais),
        mensaje: `${mensaje}\nCiudad: ${parsed.data.ciudad}`.slice(0, 1000),
      },
      extras: {
        ...qualification,
        division,
        form_name: "automatizar",
        cargo: parsed.data.cargo || null,
        ciudad: parsed.data.ciudad,
        country_code: parsed.data.pais,
      },
      lang: i18n.language,
      honeypot,
      startedAt: startedAt.current,
    });
    setSending(false);
    if (!res.ok) {
      setError(t("sFormsTransform.errors.submit"));
      return;
    }
    onClose();
    onSuccess("automatizar", undefined, { ...qualification, division, spam: res.spam });
  };


  const stepNumber = steps.indexOf(step) === -1 ? 0 : steps.indexOf(step);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] bg-black flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          <HoneypotField value={honeypot} onChange={setHoneypot} />
          {/* top bar */}
          <div className="flex items-center justify-between px-5 md:px-10 py-5">
            <div className="flex items-center gap-4">
              <img src={wordmarkWhite} alt="TECHTAIL" className="h-5 md:h-6 w-auto" />
              {step !== "intro" && step !== "final" && (
                <span className="font-dot text-[11px] md:text-xs uppercase tracking-widest text-brand-blue">
                  {t("sFormsTransform.step", { num: String(Math.min(stepNumber, total)).padStart(2, "0"), total })}
                </span>
              )}
            </div>

            {step !== "intro" && (
              <button
                type="button"
                onClick={onClose}
                className="text-white/60 text-sm hover:text-white transition-colors"
              >
                {t("sFormsTransform.close")}
              </button>
            )}
          </div>

          {/* progress */}
          <div className="h-px w-full bg-white/10">
            <div
              className="h-px bg-brand-blue transition-all duration-300"
              style={{ width: `${(stepNumber / (steps.length - 1)) * 100}%` }}
            />
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col">
            <div className="container mx-auto px-5 md:px-10 py-8 md:py-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full"
                >
                  {step === "intro" && (
                    <div className="max-w-2xl">
                      <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                        {t("sFormsTransform.intro.title")}
                      </h2>
                      <p className="mt-5 text-base md:text-lg text-white/60 max-w-xl">
                        {t("sFormsTransform.intro.subtitle")}
                      </p>
                      <button
                        type="button"
                        onClick={next}
                        className="mt-10 inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base"
                        >
                        {t("sFormsTransform.intro.cta")}
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                          <PixelArrow />
                        </span>
                      </button>
                    </div>
                  )}

                  {step === "nombre" && (
                    <Question title={t("sFormsTransform.nombre.title")}>
                      <input
                        autoFocus
                        value={a.nombre}
                        maxLength={100}
                        onChange={(e) => set("nombre", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t("sFormsTransform.nombre.placeholder")}
                        className={inputClass}
                      />
                    </Question>
                  )}

                  {step === "espacio" && (
                    <Question
                      title={t("sFormsTransform.espacio.title", {
                        saludo: a.nombre ? `, ${a.nombre.trim()}` : "",
                      })}
                    >
                      {ESPACIOS.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={espaciosLabels[i]}
                          selected={a.espacio === o}
                          onClick={() => (o === "Otra cosa" ? set("espacio", o) : pick("espacio", o))}
                        />
                      ))}
                      {a.espacio === "Otra cosa" && (
                        <input
                          autoFocus
                          value={a.espacioOtra}
                          maxLength={140}
                          onChange={(e) => set("espacioOtra", e.target.value)}
                          placeholder={t("sFormsTransform.espacio.otraPlaceholder")}
                          className={inputClass}
                        />
                      )}
                    </Question>

                  )}

                  {step === "ubicaciones" && (
                    <Question title={t("sFormsTransform.ubicaciones.title")}>
                      {UBICACIONES.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={ubicacionesLabels[i]}
                          selected={a.ubicaciones === o}
                          onClick={() => pick("ubicaciones", o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "objetivos" && (
                    <Question title={t("sFormsTransform.objetivos.title")} hint={t("sFormsTransform.objetivos.hint")}>
                      {(isAlmacen ? OBJETIVOS_ALMACEN : OBJETIVOS_RETAIL).map((o, i) => (
                        <OptionButton
                          key={o}
                          label={(isAlmacen ? objetivosAlmacenLabels : objetivosRetailLabels)[i]}
                          selected={a.objetivos.includes(o)}
                          onClick={() => toggleObjetivo(o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "plazo" && (
                    <Question title={t("sFormsTransform.plazo.title")}>
                      {PLAZOS.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={plazosLabels[i]}
                          selected={a.plazo === o}
                          onClick={() => pick("plazo", o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "decide" && (
                    <Question title={t("sFormsTransform.decide.title")}>
                      {DECIDE_OPCIONES.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={decideLabels[i]}
                          selected={a.decide === o}
                          onClick={() => pick("decide", o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "quienDecide" && (
                    <Question title={t("sFormsTransform.quienDecide.title")}>
                      {QUIEN_DECIDE.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={quienDecideLabels[i]}
                          selected={a.quienDecide === o}
                          onClick={() => pick("quienDecide", o)}
                        />
                      ))}
                    </Question>
                  )}

                  {step === "empresa" && (
                    <Question title={t("sFormsTransform.empresa.title")}>
                      <input
                        autoFocus
                        value={a.empresa}
                        maxLength={100}
                        onChange={(e) => set("empresa", e.target.value)}
                        placeholder={t("sFormsTransform.empresa.empresaPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        value={a.cargo}
                        maxLength={100}
                        onChange={(e) => set("cargo", e.target.value)}
                        placeholder={t("sFormsTransform.empresa.cargoPlaceholder")}
                        className={inputClass}
                      />
                      <input
                        value={a.ciudad}
                        maxLength={100}
                        onChange={(e) => set("ciudad", e.target.value)}
                        placeholder={t("sFormsTransform.empresa.ciudadPlaceholder")}
                        className={inputClass}
                      />
                      <CountrySelect value={a.pais} onChange={(v) => set("pais", v)} className={`${inputClass} appearance-none rounded-none`} />
                    </Question>
                  )}

                  {step === "email" && (
                    <Question
                      title={t("sFormsTransform.email.title")}
                      hint={t("sFormsTransform.email.hint")}
                    >
                      <input
                        autoFocus
                        type="email"
                        value={a.email}
                        maxLength={255}
                        onChange={(e) => set("email", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t("sFormsTransform.email.placeholder")}
                        className={inputClass}
                      />
                      <input
                        type="tel"
                        value={a.telefono}
                        maxLength={30}
                        onChange={(e) => set("telefono", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && canContinue() && next()}
                        placeholder={t("sFormsTransform.email.phonePlaceholder")}
                        className={`${inputClass} mt-4`}
                      />
                      {a.email.trim().length > 4 && !isAcceptableEmail(a.email) && (
                        <p className="text-sm text-red-400">{isDisposableEmail(a.email) ? t("sLeadFields.disposableEmail") : t("sLeadFields.invalidEmail")}</p>
                      )}
                      {a.telefono.trim().length > 5 && !isValidPhone(a.telefono) && (
                        <p className="text-sm text-red-400">{t("sLeadFields.invalidPhone")}</p>
                      )}
                    </Question>
                  )}

                  {step === "canal" && (
                    <Question title={t("sFormsTransform.canal.title")}>
                      {CANALES.map((o, i) => (
                        <OptionButton
                          key={o}
                          label={canalesLabels[i]}
                          selected={a.canal === o}
                          onClick={() => (o === "Otra cosa" ? set("canal", o) : pick("canal", o))}
                        />
                      ))}
                      {a.canal === "Otra cosa" && (
                        <input
                          autoFocus
                          value={a.canalOtra}
                          maxLength={140}
                          onChange={(e) => set("canalOtra", e.target.value)}
                          placeholder={t("sFormsTransform.canal.otraPlaceholder")}
                          className={inputClass}
                        />
                      )}
                    </Question>

                  )}

                  {step === "extra" && (
                    <Question
                      title={t("sFormsTransform.extra.title")}
                      hint={t("sFormsTransform.extra.hint")}
                    >
                      <textarea
                        autoFocus
                        value={a.extra}
                        maxLength={1000}
                        rows={4}
                        onChange={(e) => set("extra", e.target.value)}
                        placeholder={t("sFormsTransform.extra.placeholder")}
                        className={`${inputClass} resize-none text-lg`}
                      />
                    </Question>
                  )}

                  {step === "final" && (
                    <div className="max-w-2xl">
                      <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                        {t("sFormsTransform.final.title", {
                          saludo: a.nombre ? `, ${a.nombre.trim()}` : "",
                        })}
                      </h2>
                      <p className="mt-5 text-base md:text-lg text-white/60">
                        {t("sFormsTransform.final.subtitle")}
                      </p>
                      <a
                        href={dosier.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-10 inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-base"
                      >
                        {t("sFormsTransform.final.cta")}
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                          <PixelArrow />
                        </span>
                      </a>
                      <p className="mt-10 text-sm md:text-base text-white/50">
                        {t("sFormsTransform.final.footer")}
                      </p>
                      <button
                        type="button"
                        onClick={onClose}
                        className="mt-2 font-dot text-xs uppercase tracking-widest text-brand-blue underline underline-offset-4"
                      >
                        {t("sFormsTransform.final.visita")}
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* bottom nav */}
          {step !== "intro" && step !== "final" && (
            <div className="px-5 md:px-10 pb-8 pt-2 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={back}
                className="text-sm text-white/50 hover:text-white transition-colors"
              >
                {t("sFormsTransform.nav.back")}
              </button>
              <div className="flex items-center gap-4">
                {error && <span className="text-sm text-white/70">{error}</span>}
                <button
                  type="button"
                  disabled={!canContinue() || sending}
                  onClick={step === "extra" ? submit : next}
                  className="inline-flex items-center gap-3 rounded-none bg-brand-blue pl-6 pr-2 py-2 text-black text-sm md:text-base disabled:opacity-40"
                >
                  {step === "extra" ? (sending ? t("sFormsTransform.nav.sending") : t("sFormsTransform.nav.send")) : t("sFormsTransform.nav.continue")}
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-none bg-black text-brand-blue">
                    <PixelArrow />
                  </span>
                </button>
              </div>
            </div>
          )}
          {step === "intro" && (
            <div className="absolute inset-x-0 bottom-0 z-10 flex justify-center px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4">
              <button
                type="button"
                aria-label={t("sFormsTransform.close")}
                onClick={onClose}
                className="h-12 w-12 flex items-center justify-center border border-white/30 text-white hover:text-brand-blue transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>

            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TransformForm;
